import { useCallback, useEffect, useRef, useSyncExternalStore } from "react"

// Public name of the URL search param. Keeping this in one place
// means the pill nav, the page, and any deep link customer-support
// hands out all agree on the same key. Kept short ("platform") so
// hand-pasted URLs stay short and copy/paste reliably.
export const STATUS_FILTER_PARAM = "platform"

// Sentinel for "show every platform". Stored in the URL as the
// absence of the param rather than a literal `=all` so the canonical
// landing page is `/` -- copy/pasted URLs without the param land on
// the same content as a deliberate "all platforms" click.
export const STATUS_FILTER_ALL = "all"

// Prefix for the hash anchor we jump to when the customer clicks a
// category pill. Centralised so the secondary nav, the per-category
// row, and any future deep-link consumer all agree on the same id
// shape.
export const STATUS_CATEGORY_HASH_PREFIX = "category-"

// useStatusFilter reads / writes the active platform filter from
// the URL. The URL is the single source of truth so:
//
//   - Customer-support links like `?platform=propcloud` land on
//     the right view without React having to "rehydrate" some
//     in-memory state.
//   - The browser back / forward buttons walk through previously
//     selected platforms naturally (we use pushState).
//   - Deep links with a hash (`?platform=propcloud#service-...`)
//     work because the page renders the matching DOM id on first
//     paint, so the browser's native scroll-to-hash kicks in.
//
// Implemented with useSyncExternalStore so the hook plays nicely
// with React 18's concurrent rendering -- no setState-in-render
// races when the URL changes mid-render.
export function useStatusFilter(): {
  platformId: string
  setPlatformId: (id: string) => void
} {
  const platformId = useSyncExternalStore(
    subscribeToUrl,
    readPlatformParam,
    readPlatformParamServer,
  )
  return { platformId, setPlatformId }
}

// useActiveCategoryId returns the category id encoded in the URL
// hash (without the `category-` prefix), or "" when there is none.
// Drives the pressed visual state of the secondary nav so a
// customer can tell which jump-link they last followed without us
// also tracking a separate React state.
//
// Implemented on top of the same useSyncExternalStore subscription
// the platform filter uses -- our internal jump setter dispatches
// the shared event and the standard hashchange / popstate listeners
// cover everything else.
export function useActiveCategoryId(): string {
  return useSyncExternalStore(
    subscribeToUrl,
    readActiveCategoryId,
    readActiveCategoryIdServer,
  )
}

// useJumpToCategory returns a setter that scrolls the page to a
// category row and updates the URL hash so the destination is
// shareable. Wrapped in useCallback because consumers pass it into
// memoised pill components.
//
// Accepts an optional sub-category id so the caller can request
// "open the parent and scroll to the sub-category inside it" in a
// single hop. The hash always points at the leaf (parent or child)
// so back/forward buttons remember exactly which row the customer
// landed on. The accordion is responsible for ensuring its
// `#category-…` panel is open when this fires; we just point at
// the DOM id and let the layout sort itself out.
export function useJumpToCategory(): (
  categoryId: string,
  subCategoryId?: string,
) => void {
  return useCallback((categoryId: string, subCategoryId?: string) => {
    if (typeof window === "undefined") return
    const targetId = subCategoryId ?? categoryId
    const hash = `${STATUS_CATEGORY_HASH_PREFIX}${targetId}`
    const url = new URL(window.location.href)
    url.hash = `#${hash}`
    if (url.toString() !== window.location.href) {
      window.history.pushState(null, "", url.toString())
      window.dispatchEvent(new Event(STATUS_FILTER_EVENT))
    }
    // Defer the scroll one frame so the accordion has a chance to
    // mount its content panel before we resolve the target. Without
    // this, jumping to a sub-category inside a closed accordion
    // resolves to a missing element and silently no-ops.
    requestAnimationFrame(() => {
      const el = document.getElementById(hash)
      if (el) scrollElementToTop(el)
    })
  }, [])
}

// useScrollToHashWhenReady lands deep links like
// `?platform=foo#service-bar` on the right row. Called from the
// page with `ready` flipped true the moment the data renders;
// before that the target id doesn't exist in the DOM and the
// browser's native hash-scroll silently no-ops on initial paint.
//
// Polls a handful of animation frames after `ready` becomes true
// so the scroll is robust against:
//   - data populating synchronously but the DOM not yet committed
//     when the first effect runs
//   - React 18 strict-mode dev double-mount cancelling the queued
//     callback before the element is available
//   - layout shifts after the first paint (sparkline SVGs sizing
//     up, fonts swapping in) that change the target's final y.
//
// Runs at most once per page load so a subsequent data refresh
// (poll cycle) doesn't yank the customer back to a stale anchor.
export function useScrollToHashWhenReady(ready: boolean): void {
  const fired = useRef(false)
  useEffect(() => {
    if (!ready || fired.current) return
    if (typeof window === "undefined") return
    if (!window.location.hash) {
      fired.current = true
      return
    }
    const id = window.location.hash.slice(1)
    let cancelled = false
    let framesLeft = SCROLL_TO_HASH_RETRY_FRAMES

    const tick = () => {
      if (cancelled) return
      const el = document.getElementById(id)
      if (el) {
        scrollElementToTop(el)
        fired.current = true
        return
      }
      framesLeft -= 1
      if (framesLeft <= 0) {
        // Element never showed up (id renamed or trimmed by the
        // backend); give up silently rather than scrolling to a
        // fallback that would surprise the customer.
        fired.current = true
        return
      }
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
    return () => {
      cancelled = true
    }
  }, [ready])
}

// SCROLL_TO_HASH_RETRY_FRAMES caps the retry budget at ~16 frames
// (~270 ms at 60 fps). Long enough to outlast strict-mode
// double-mounts and async font / SVG layout settle, short enough
// that a missing element doesn't keep posting rAFs forever.
const SCROLL_TO_HASH_RETRY_FRAMES = 16

// scrollElementToTop scrolls the page so `el` lands just below the
// sticky nav. On the public status page the document itself is
// the scroll container (see styles.css), so `window.scrollTo` is
// usually the right call. We still try the nested-container path
// first because:
//
//   1. A future drawer / modal could introduce a genuine nested
//      scroll context, and we'd rather adapt automatically than
//      hard-code "always scroll the window".
//   2. `el.scrollIntoView` ensures any horizontal scrolling
//      container (the category pill row's horizontal overflow,
//      for instance) brings its slice of `el` into view too.
//
// The offset (sticky-nav height) is subtracted so the destination
// row isn't covered by the platform / category pill bars.
function scrollElementToTop(el: HTMLElement): void {
  el.scrollIntoView({ block: "start", behavior: "auto" })
  const offset = STICKY_NAV_OFFSET_PX
  const container = findScrollableAncestor(el)
  if (container) {
    const elRect = el.getBoundingClientRect()
    const cRect = container.getBoundingClientRect()
    const target = container.scrollTop + (elRect.top - cRect.top) - offset
    container.scrollTo({ top: Math.max(0, target), behavior: "auto" })
    return
  }
  const top = el.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top: Math.max(0, top), behavior: "auto" })
}

// findScrollableAncestor returns the nearest ancestor that actually
// scrolls vertically (overflow-y auto/scroll *and* content taller
// than the box). Returns null when the document itself is the
// scroll root, in which case window.scrollTo is the right call.
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement
  while (node && node !== document.body && node !== document.documentElement) {
    const style = getComputedStyle(node)
    const oy = style.overflowY
    if (
      (oy === "auto" || oy === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}

// STICKY_NAV_OFFSET_PX is the y-offset reserved at the top of the
// viewport when scrolling to a deep-linked row. The platform pill
// nav is now the only thing pinned to the top of the page (the
// category jump bar moved into a left sidebar), so the offset is
// just that pill row's height plus a hair of breathing room. The
// per-element `scroll-mt-*` utility on each target row stacks on
// top of this constant, so accordion items / service rows can opt
// for a wider gap when their sticky heading is also in play.
const STICKY_NAV_OFFSET_PX = 64

// readPlatformParam returns the active platform from the URL, or
// the all-platforms sentinel when the param is absent / blank.
// Lives at module scope (not inside the hook) so useSyncExternalStore
// can call it on every render without allocating a new closure.
function readPlatformParam(): string {
  if (typeof window === "undefined") return STATUS_FILTER_ALL
  const v = new URLSearchParams(window.location.search).get(STATUS_FILTER_PARAM)
  return v && v.trim() !== "" ? v : STATUS_FILTER_ALL
}

// SSR-safe constant. The status page is a pure CSR app so we never
// actually hit this branch; it's here so the hook signature is
// honest about its server fallback if we ever pre-render.
function readPlatformParamServer(): string {
  return STATUS_FILTER_ALL
}

// readActiveCategoryId pulls the category id out of the URL hash
// (e.g. `#category-gis-basemaps` -> `gis-basemaps`). Returns "" for
// any other hash shape -- service deep links use `#service-…` and
// shouldn't make a category pill light up.
function readActiveCategoryId(): string {
  if (typeof window === "undefined") return ""
  const hash = window.location.hash.slice(1)
  if (!hash.startsWith(STATUS_CATEGORY_HASH_PREFIX)) return ""
  return hash.slice(STATUS_CATEGORY_HASH_PREFIX.length)
}

function readActiveCategoryIdServer(): string {
  return ""
}

// subscribeToUrl wires useSyncExternalStore to every event that
// can change the URL out from under us:
//
//	- popstate: back / forward buttons.
//	- hashchange: the user typing a new fragment in the URL bar.
//	- our custom event: pushState calls don't natively fire
//	  popstate, so the setter dispatches this so subscribers
//	  re-read.
//
// Together these cover both the platform query param and the
// category hash, so `useStatusFilter` and `useActiveCategoryId`
// stay live without each one having to register its own listeners.
function subscribeToUrl(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {}
  window.addEventListener("popstate", onChange)
  window.addEventListener("hashchange", onChange)
  window.addEventListener(STATUS_FILTER_EVENT, onChange)
  return () => {
    window.removeEventListener("popstate", onChange)
    window.removeEventListener("hashchange", onChange)
    window.removeEventListener(STATUS_FILTER_EVENT, onChange)
  }
}

// Custom DOM event our setter dispatches so the
// useSyncExternalStore subscriber re-reads the URL. Stays
// internal -- consumers go through setPlatformId.
const STATUS_FILTER_EVENT = "placepoint:status-filter-changed"

// setPlatformId pushes a new entry on the history stack so the
// browser back button walks through previously selected platforms
// (matches what customers expect on tabs / pills). Replacing instead
// of pushing would make "back" leave the page entirely after every
// click -- worse UX for casual exploration.
function setPlatformId(id: string): void {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  if (id === STATUS_FILTER_ALL) {
    url.searchParams.delete(STATUS_FILTER_PARAM)
  } else {
    url.searchParams.set(STATUS_FILTER_PARAM, id)
  }
  // Clear any trailing hash on a deliberate platform switch so the
  // browser doesn't re-scroll to a stale anchor from a previous
  // deep link. The hash is preserved on initial load (no setter
  // call), only reset when the customer chooses a new pill.
  url.hash = ""
  if (url.toString() === window.location.href) return
  window.history.pushState(null, "", url.toString())
  window.dispatchEvent(new Event(STATUS_FILTER_EVENT))
}
